import { useEffect } from "react";
import { Box, Button, Group } from "@mantine/core";
import {
  DataTable, EmptyRow, GhostButton, Prompt, Screen, type Column,
  LoadError,
}  from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import type { ApiKey } from "../types/domain";
import css from "./screens.module.css";

const COLUMNS: Column[] = [
  { key: "name", label: "name", width: "1.2fr" },
  { key: "masked", label: "key", width: "1.5fr" },
  { key: "scopes", label: "scopes", width: "1.5fr" },
  { key: "calls", label: "calls", width: "0.8fr" },
  { key: "created", label: "created", width: "0.8fr" },
];

export default function Keys() {
  const openModal = useUiStore((s) => s.openModal);
  const openDetail = useUiStore((s) => s.openDetail);
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const keys = useConsoleStore((s) => s.keys);
  const loadError = useConsoleStore((s) => s.errors.keys);
  const newSecret = useConsoleStore((s) => s.newSecret);
  const dismissSecret = useConsoleStore((s) => s.dismissSecret);
  const load = useConsoleStore((s) => s.loadKeys);

  useEffect(() => {
    if (workspaceId) void load();
  }, [workspaceId, load]);

  return (
    <Screen>
      <Prompt
        command="vf keys ls"
        action={<GhostButton onClick={() => openModal("createKey")}>+ create key</GhostButton>}
      />
      <LoadError message={loadError} />

      {newSecret ? (
        <div className={css.keyReveal}>
          <div className={css.keyRevealHead}>
            <span>! copy now — shown once</span>
            <button type="button" className={css.dismiss} onClick={dismissSecret}>
              [dismiss]
            </button>
          </div>
          <div className={css.keyRevealValue}>{newSecret}</div>
        </div>
      ) : null}

      <DataTable<ApiKey>
        fontSize="12.5px"
        columns={COLUMNS}
        rows={keys}
        rowKey={(k) => k.id}
        onRowClick={(k) => openDetail("key", k.id)}
        empty={
          <EmptyRow
            title="no API keys yet"
            body="Keys are workspace-scoped and carry capability scopes plus a grant pattern. Create one to call the API from your app."
            action={
              <Button fz="13px" fw={600} onClick={() => openModal("createKey")}>
                ▸ create your first key
              </Button>
            }
          />
        }
        renderCell={(k, key) => {
          switch (key) {
            case "name":
              return (
                <Box>
                  <Box c="var(--vf-text)">{k.name}</Box>
                  <Box c="var(--vf-dim)" fz="11px">
                    {k.grants}
                  </Box>
                </Box>
              );
            case "masked":
              return <span style={{ color: "var(--vf-sec)" }}>{k.masked}</span>;
            case "scopes":
              return (
                <Group gap={5} wrap="wrap">
                  {k.scopes.map((s) => (
                    <span key={s} className={css.scopeChip}>
                      {s}
                    </span>
                  ))}
                </Group>
              );
            case "calls":
              return <span style={{ color: "var(--vf-sec)" }}>{k.calls}</span>;
            case "created":
              return <span style={{ color: "var(--vf-sec)" }}>{k.created}</span>;
            default:
              return null;
          }
        }}
      />
    </Screen>
  );
}
